"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Loader2, AlertCircle } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useStore } from "@/lib/store";

const WORKSPACE_COLORS = [
  { name: "Indigo", value: "#5e6ad2" },
  { name: "Red", value: "#eb5757" },
  { name: "Orange", value: "#f2994a" },
  { name: "Yellow", value: "#f2c94c" },
  { name: "Green", value: "#26b24b" },
  { name: "Blue", value: "#4ea7fc" },
  { name: "Purple", value: "#a55cff" },
  { name: "Pink", value: "#ff8ae2" },
  { name: "Teal", value: "#4cc3c7" },
];

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 48)
    .replace(/^-+|-+$/g, "");
}

export function CreateWorkspaceModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const createWorkspace = useStore((s) => s.createWorkspace);
  const setActiveWorkspace = useStore((s) => s.setActiveWorkspace);

  const [name, setName] = useState("");
  const [icon, setIcon] = useState("◆");
  const [color, setColor] = useState(WORKSPACE_COLORS[0].value);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<"client" | "dev" | "personal">("client");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setIcon("◆");
      setColor(WORKSPACE_COLORS[0].value);
      setDescription("");
      setCategory("client");
      setError(null);
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open]);

  const slug = useMemo(() => slugify(name), [name]);
  const slugValid = slug.length >= 2;
  const canSubmit = name.trim().length > 0 && slugValid && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const ws = await createWorkspace({
        name: name.trim(),
        icon,
        color,
        description: description.trim(),
        category,
      });
      setActiveWorkspace(ws.id);
      onClose();
      // Navigate to the new workspace so the UI jumps straight into it
      // instead of staying on whichever workspace the user was just viewing.
      if (typeof window !== "undefined") {
        window.location.assign(`/${ws.slug}`);
      }
    } catch (err) {
      setError(serializeError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose} width={520}>
      <div
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            void submit();
          }
        }}
      >
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--border)]">
          <Plus size={14} className="text-[var(--accent)]" />
          <h3 className="text-[13.5px] font-semibold">New workspace</h3>
          <span className="ml-auto text-[10.5px] text-[var(--fg-muted)]">
            One per client / product
          </span>
        </div>

        <div className="p-5 space-y-4">
          {/* Live preview */}
          <div className="flex items-center gap-3 p-3 rounded-md bg-[var(--bg-secondary)] border border-[var(--border)]">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-[16px] shrink-0"
              style={{
                background: `linear-gradient(135deg, ${color}, color-mix(in srgb, ${color} 55%, #000))`,
              }}
            >
              {icon || "◆"}
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold truncate">
                {name.trim() || "Your workspace name"}
              </div>
              <div className="text-[11px] text-[var(--fg-tertiary)] font-mono truncate">
                {slug || "slug-preview"}
              </div>
            </div>
          </div>

          {/* Name */}
          <Field label="Name">
            <input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Corp"
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
              maxLength={80}
            />
            {name && !slugValid && (
              <p className="text-[10.5px] text-[var(--orange)] mt-1">
                Needs at least 2 letters or digits.
              </p>
            )}
          </Field>

          {/* Icon + color in a row */}
          <div className="grid grid-cols-[110px_1fr] gap-3">
            <Field label="Icon">
              <input
                value={icon}
                onChange={(e) => setIcon(e.target.value.slice(0, 2))}
                placeholder="◆"
                className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[14px] text-center outline-none focus:border-[var(--accent)]"
                maxLength={2}
              />
              <p className="text-[10px] text-[var(--fg-muted)] mt-1">
                Emoji or symbol
              </p>
            </Field>
            <Field label="Color">
              <div className="flex flex-wrap gap-1.5">
                {WORKSPACE_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setColor(c.value)}
                    className="w-7 h-7 rounded-md border-2 transition-all"
                    style={{
                      background: c.value,
                      borderColor: color === c.value ? "var(--fg)" : "transparent",
                      transform: color === c.value ? "scale(1.1)" : "scale(1)",
                    }}
                    title={c.name}
                  />
                ))}
              </div>
            </Field>
          </div>

          {/* Category */}
          <Field label="Category">
            <div className="flex gap-1.5">
              {(
                [
                  { id: "client", label: "Client", hint: "Agents for real clients" },
                  { id: "dev", label: "Dev", hint: "Agents that help build an app" },
                  { id: "personal", label: "Personal", hint: "Just for you" },
                ] as const
              ).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id)}
                  className={
                    "flex-1 px-3 py-2 rounded-md border text-left transition-colors " +
                    (category === c.id
                      ? "bg-[var(--accent-bg)] border-[var(--accent)] text-[var(--accent)]"
                      : "bg-[var(--bg-tertiary)] border-[var(--border)] hover:border-[var(--border-strong)] text-[var(--fg-secondary)]")
                  }
                >
                  <div className="text-[12px] font-semibold">{c.label}</div>
                  <div className="text-[10px] text-[var(--fg-muted)] mt-0.5">{c.hint}</div>
                </button>
              ))}
            </div>
          </Field>

          {/* Description */}
          <Field label="Description" optional>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What the workspace is for. One line is fine."
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[12.5px] outline-none focus:border-[var(--accent)] resize-none"
              rows={2}
              maxLength={280}
            />
          </Field>

          {/* Error */}
          {error && (
            <div className="rounded-md border border-[var(--red)]/30 bg-[var(--red)]/8 p-3 flex items-start gap-2">
              <AlertCircle size={13} className="text-[var(--red)] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-[var(--fg)]">
                  Couldn&apos;t create workspace
                </div>
                <pre className="text-[11.5px] text-[var(--fg-secondary)] mt-1 whitespace-pre-wrap break-words font-mono">
                  {error}
                </pre>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-[var(--border)] bg-[var(--bg-secondary)]">
          <div className="text-[11px] text-[var(--fg-muted)]">
            <kbd className="kbd">⌘</kbd> <kbd className="kbd">Enter</kbd> to create
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-3 py-1.5 rounded-md text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => void submit()}
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-40 text-white text-[12.5px] font-medium"
            >
              {submitting ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Plus size={12} />
              )}
              {submitting ? "Creating…" : "Create workspace"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Field({
  label,
  optional,
  children,
}: {
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10.5px] uppercase tracking-wide text-[var(--fg-muted)] font-medium flex items-center gap-1">
        {label}
        {optional && <span className="text-[var(--fg-muted)] normal-case">· optional</span>}
      </div>
      {children}
    </div>
  );
}

/** Flatten any error shape into a readable multi-line string. */
function serializeError(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "string") return err;
  if (err && typeof err === "object") {
    const e = err as Record<string, unknown>;
    const parts: string[] = [];
    if (e.message) parts.push(String(e.message));
    if (e.code) parts.push(`code: ${e.code}`);
    if (e.details) parts.push(`details: ${e.details}`);
    if (e.hint) parts.push(`hint: ${e.hint}`);
    if (parts.length) return parts.join("\n");
    try {
      return JSON.stringify(err, null, 2);
    } catch {
      /* fall through */
    }
  }
  return String(err);
}
